import { stashKnowledgePrefill, peekKnowledgePrefill } from "../../utils/knowledgePrefill";
import {
  expectedWizardStepForChapter,
  wizardStepIndex,
} from "./guideProgress";
import { readGuideState } from "./guideStorage";
import {
  ADD_KNOWLEDGE_FIRST_APP,
  getScript,
} from "./scripts/addKnowledgeFirstApp";

const ADD_CHAPTERS = new Set([
  "intent",
  "create-app",
  "choose-how",
  "supply",
]);

const WIZARD_STEPS = new Set(["scope", "anchor", "pick", "source"]);

/** Prefer the later of two wizard steps (invalid steps lose). */
export function preferLaterWizardStep(a, b) {
  const ai = wizardStepIndex(a);
  const bi = wizardStepIndex(b);
  if (ai < 0 && bi < 0) return "anchor";
  if (ai < 0) return String(b || "").trim() || "anchor";
  if (bi < 0) return String(a || "").trim() || "anchor";
  return ai >= bi ? String(a).trim() : String(b).trim();
}

/**
 * Rebuild Add-knowledge prefill from persisted guide progress so refresh
 * restores the user to the correct wizard step (entity + notes included).
 */
export function buildGuideWizardPrefill(guideState) {
  if (!guideState?.scriptId) return null;
  const chapterId = String(guideState.chapterId || "");
  if (!ADD_CHAPTERS.has(chapterId)) return null;

  const script = getScript(guideState.scriptId) || ADD_KNOWLEDGE_FIRST_APP;
  const entityId = String(guideState.entityId || "").trim();
  const entityName = String(
    guideState.entityName || script.sampleName || ""
  ).trim();
  const notes = String(script.sampleNotes || "");
  const fromPersisted = String(guideState.wizardStep || "").trim();
  const fromChapter = String(expectedWizardStepForChapter(chapterId) || "").trim();
  let step = preferLaterWizardStep(
    WIZARD_STEPS.has(fromPersisted) ? fromPersisted : "",
    WIZARD_STEPS.has(fromChapter) ? fromChapter : "anchor"
  );

  // Notes were loaded (or user is on supply) — always reopen the paste box
  // with CPH text and the Extract cue, never the empty create/pick screens.
  const notesReady = Boolean(guideState.sampleNotesLoaded) || chapterId === "supply";
  if (notesReady && entityId) {
    step = preferLaterWizardStep(step, "source");
  }

  if (entityId) {
    const startStep = step === "scope" ? "anchor" : step;
    return {
      scopeType: "entity",
      id: entityId,
      name: entityName || script.sampleName,
      suggestedType: "application",
      startStep,
      pasteText: notes,
      nextStepHint:
        startStep === "source" && notes ? "extract" : undefined,
    };
  }

  // Still creating — land on create-entity with CPH name + queued notes.
  return {
    scopeType: "entity",
    intent: "create",
    suggestedName: entityName || script.sampleName,
    suggestedType: "application",
    startStep: "anchor",
    pasteText: notes,
  };
}

/**
 * Stash resume prefill when session prefill is empty — or upgrade a stale
 * session prefill (e.g. still on "anchor") when persisted guide progress
 * is further ahead (source + notes).
 */
export function ensureGuideWizardPrefill(guideState = readGuideState()) {
  const built = buildGuideWizardPrefill(guideState);
  const existing = peekKnowledgePrefill();
  if (!built) return existing;

  if (!existing) {
    stashKnowledgePrefill(built);
    return built;
  }

  const existingIdx = wizardStepIndex(existing.startStep);
  const builtIdx = wizardStepIndex(built.startStep);
  const shouldUpgrade =
    builtIdx > existingIdx ||
    (Boolean(built.id) && built.id !== String(existing.id || "")) ||
    (Boolean(built.pasteText) && !String(existing.pasteText || "").trim()) ||
    (Boolean(built.nextStepHint) && !existing.nextStepHint) ||
    (existing.intent === "create" && Boolean(built.id));

  if (shouldUpgrade) {
    stashKnowledgePrefill(built);
    return built;
  }
  return existing;
}

export function guideNeedsAddKnowledgeRoute(guideState = readGuideState()) {
  const chapterId = String(guideState?.chapterId || "");
  // Job already queued → Inbox owns the next step, not Add knowledge.
  if (String(guideState?.jobId || "").trim()) return false;
  return (
    chapterId === "create-app" ||
    chapterId === "choose-how" ||
    chapterId === "supply"
  );
}

/** Building / review: refresh should land on Inbox, not Add knowledge. */
export function guideNeedsInboxRoute(guideState = readGuideState()) {
  const chapterId = String(guideState?.chapterId || "");
  if (chapterId === "processing" || chapterId === "review") return true;
  // Catch-up: Extract already queued but chapter still on supply.
  if (chapterId === "supply" && String(guideState?.jobId || "").trim()) {
    return true;
  }
  return false;
}
